import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { DispatchContext, StateContext } from '../../AppContext'
import styles from './aside.module.scss'
import UserDetails from 'src/pages/users/UserDetails'
import CompanyDetails from 'src/pages/companies/CompanyDetails'
import LocationDetails from 'src/pages/location/LocationDetails'
import AttributeDetails from 'src/pages/attributes/AttributeDetails'
import AccountsDetails from 'src/pages/accounts/AccountsDetails'
import FixedTripsDetails from 'src/pages/fixedTrips/FixedTripsDetails'
import OrdersDetails from 'src/pages/orders/OrdersDetails'
import OrderDetails from 'src/pages/orders/OrderDetails'
import EditOrder from 'src/pages/orders/EditOrder'
import AddressDetails from 'src/pages/addresses/AddressesDetails'
import ToggleVouchers from 'src/pages/vouchers/ToggleVouchers'
import VouchersDetails from 'src/pages/vouchers/VouchersDetails'
import DiscountCodeDetails from 'src/pages/discountCode/DiscountCodeDetails'
import Settings from 'src/components/settingsModal/SettingsModal'
import { IState } from 'src/Interface';
import { createPortal } from 'react-dom';
import AddressPOIDetails from 'src/pages/addresses/AddressesPOIDetails'


const ModalComponents = {
  UserDetails: () => UserDetails,
  CompanyDetails: () => CompanyDetails,
  LocationDetails: () => LocationDetails,
  AttributeDetails: () => AttributeDetails,
  FixedTripsDetails: () => FixedTripsDetails,
  AccountsDetails: () => AccountsDetails,
  OrdersDetails: () => OrdersDetails,
  OrderDetails: () => OrderDetails,
  EditOrder: () => EditOrder,
  ToggleVouchers: () => ToggleVouchers,
  AddressDetails: () => AddressDetails,
  VouchersDetails: () => VouchersDetails,
  VoucherOrderDetails: () => OrderDetails,
  DiscountCodeDetails: () => DiscountCodeDetails,
  Settings: () => Settings,
  AddressPOIDetails: () => AddressPOIDetails,
}

function useOutsideAlerter(ref, show, showModal) {
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const close = useCallback(() => { dispatch({ type: 'SHOW_ASIDE', show: false }) }, [dispatch])
  const modal = document.getElementById('modal-module')

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target) && !showModal) {
        close()
      }
    }
    // Bind the event listener
    if (show) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [close, showModal, ref, show, modal]);
}


// eslint-disable-next-line react/prop-types
const Aside = React.memo(function Aside() {
  const { aside: { asideType, size, show, ...props }, modal }: IState = useContext(StateContext)
  const [modalSize, setModalSize] = useState('')

  useEffect(() => {
    if (size) {
      setModalSize('small')
    } else {
      setTimeout(() => setModalSize(''), 400)
    }
  }, [size])

  const [shouldRender, setRender] = useState(show);
  const container = useRef(null)
  useOutsideAlerter(container, show, modal.showModal);

  const dispatch: React.Dispatch<any> = useContext(DispatchContext)

  const close = useCallback(() => {
    dispatch({ type: 'SHOW_ASIDE', show: false })
  }, [dispatch])

  const closeModal = useCallback((e?: any) => {
    if (e.key === 'Escape') {
      close()
    }
  }, [close])

  useEffect(() => {
    if (show) {
      window.scrollTo(0, 0)
      setRender(true);
      window.addEventListener('keydown', closeModal);
    }
    else {
      window.removeEventListener('keydown', closeModal)
    }
  }, [close, closeModal, show])

  const onAnimationEnd = () => {
    if (!show) {
      setRender(false);
    }
  };

  const Component: any = useMemo(() => {
    if (asideType && ModalComponents[asideType]) {
      return ModalComponents[asideType]()
    }
  }, [asideType])

  return createPortal(
    shouldRender ?
      <aside onClick={(e) => e.stopPropagation()} ref={container} onAnimationEnd={onAnimationEnd} className={`${styles.aside} ${show ? styles.slideIn : styles.slideOut} ${modalSize === 'small' ? styles.small : ''}`}>
        {asideType && <Component close={close} {...props} />}
      </aside> : null
    , document.querySelector('.modal'))
});


export default Aside;